import * as ex from 'excalibur';
import { TILE_SIZE, GRID_WIDTH, GRID_HEIGHT, WORLD_WIDTH, WORLD_HEIGHT } from './config/constants';
import { Grid } from './core/grid';
import { Entity } from './core/entity';
import { Animal } from './core/animal';
import { Plant } from './core/plant';
import { CameraController } from './core/camera';
import { AnimalFactory } from './factories/animalFactory';
import { PlantFactory } from './factories/plantFactory';
import { AnimalType, PlantType } from './types/enums';
import { ActionPool, GridPositionPool } from './core/objectPool';
import { SpriteManager } from './managers/spriteManager';
import { MovementSystem } from './core/MovementSystem';
import { ParticleManager } from './managers/particleManager';
import { PostProcessorManager } from './managers/postProcessorManager';
import { EmotionSystem } from './core/EmotionSystem';
import { AudioManager } from './managers/audioManager';

export class Game extends ex.Scene {
  // Game elements
  public grid: Grid;
  private worldEntities: Entity[] = [];
  private cameraController: CameraController;
  private gameRunning: boolean = true;
  private debugMode: boolean = false;
  
  // Speed control variables
  private gamePaused: boolean = false;
  private gameSpeed: number = 1.0; // 1.0 = normal speed
  
  // UI elements
  private statsText: ex.Label;
  private fpsCounter: ex.Label;
  private debugToggle: ex.Actor;
  
  // Speed control UI elements
  private pauseButton: HTMLElement | null = null;
  private speedButtons: {[key: string]: HTMLElement | null} = {};

  constructor() {
    super();
  }

  // Initialization
  public onInitialize(engine: ex.Engine) {
    console.log('Initializing game...');
    
    // Initialize sprite manager first
    SpriteManager.initialize();
    
    // Initialize the movement system and make it globally accessible
    (window as any).__movementSystem = MovementSystem.getInstance();
    // Movement system is already initialized with the default duration from constants
    
    // Initialize the particle manager
    const particleManager = ParticleManager.getInstance();
    particleManager.initialize(engine);
    
    // Initialize the post-processor manager
    const postProcessorManager = PostProcessorManager.getInstance();
    postProcessorManager.initialize(engine);
    
    // Initialize the emotion system
    const emotionSystem = EmotionSystem.getInstance();
    
    // Initialize the audio manager
    // We'll initialize it now but the actual audio context will initialize on first sound play
    // to avoid autoplay restrictions in browsers
    const audioManager = AudioManager.getInstance();
    // Pass the engine reference to allow spatial audio calculations
    audioManager.initialize(engine);
    
    // Initialize the camera controller
    this.cameraController = new CameraController(engine);
    
    // Create the grid
    this.grid = new Grid(this);
    
    // Initialize the grid with a procedural terrain
    this.grid.initialize();
    
    // Draw debug grid lines if in debug mode
    if (this.debugMode) {
      this.grid.drawDebugGrid();
    }
    
    // Create UI for displaying stats
    this.createUI(engine);
    
    // Populate the world with plants
    this.populatePlants();
    
    // Populate the world with animals
    this.populateAnimals();
    
    // Set up input handlers
    this.setupInputHandlers(engine);
    
    // Initialize speed controls
    this.initializeSpeedControls();
    
    // Initialize visual effect controls
    this.initializeVisualEffectControls();
    
    // Initialize sound controls
    this.initializeSoundControls();
    
    // Start game loop
    this.gameRunning = true;
    
    console.log('Game initialization complete');
  }
  
  // Initialize visual effect controls for particles and post-processing
  private initializeVisualEffectControls() {
    // Set up minimize/maximize functionality for the effects panel
    const minimizeButton = document.getElementById('minimize-effects');
    const effectsContent = document.getElementById('effects-content');
    
    if (minimizeButton && effectsContent) {
      minimizeButton.addEventListener('click', () => {
        effectsContent.classList.toggle('minimized');
        if (effectsContent.classList.contains('minimized')) {
          minimizeButton.textContent = '[+]';
        } else {
          minimizeButton.textContent = '[-]';
        }
      });
    }
    
    // We'll set up weather controls in initializeSoundControls
    // to avoid having duplicate click handlers
    // (original event handlers are kept for reference)
    const rainButtonVisual = document.getElementById('weather-rain');
    const snowButtonVisual = document.getElementById('weather-snow');
    
    // This will be overridden in initializeSoundControls
    if (rainButtonVisual) {
      console.log("Visual effects: Found rain button, will override in sound controls");
    }
    
    if (snowButtonVisual) {
      console.log("Visual effects: Found snow button, will override in sound controls");
    }
    
    // Add standard effects buttons
    this.setupPostProcessorButton('effect-retro', 'toggleRetroScreen');
    this.setupPostProcessorButton('effect-pixel', 'togglePixelArt');
    this.setupPostProcessorButton('effect-bloom', 'toggleBloom');
    
    // Special case for the existing night mode button 
    const nightButton = document.getElementById('effect-night');
    if (nightButton) {
      nightButton.addEventListener('click', () => {
        const postProcessorManager = PostProcessorManager.getInstance();
        const isActive = postProcessorManager.toggleNight();
        if (isActive) {
          nightButton.classList.add('active');
        } else {
          nightButton.classList.remove('active');
        }
      });
    }
    
    // Add time of day buttons
    this.setupPostProcessorButton('time-dawn', 'toggleDawn', 'time');
    this.setupPostProcessorButton('time-day', 'toggleDay', 'time');  
    this.setupPostProcessorButton('time-sunset', 'toggleSunset', 'time');
    
    // Add season buttons
    this.setupPostProcessorButton('season-spring', 'toggleSpring', 'season');
    this.setupPostProcessorButton('season-summer', 'toggleSummer', 'season');
    this.setupPostProcessorButton('season-fall', 'toggleFall', 'season');
    this.setupPostProcessorButton('season-winter', 'toggleWinter', 'season');
    
    // Add a keyboard shortcut for clearing all effects (ESC key)
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        // Clear all effects
        ParticleManager.getInstance().stopWeather();
        PostProcessorManager.getInstance().clearAllEffects();
        
        // Remove active class from all effect buttons
        document.querySelectorAll('#visual-effects .effect-btn').forEach(btn => {
          btn.classList.remove('active');
        });
      }
    });
  }
  
  /**
   * Helper method to set up post-processor toggle buttons
   */
  private setupPostProcessorButton(buttonId: string, methodName: string, groupPrefix: string = ''): void {
    const button = document.getElementById(buttonId);
    if (button) {
      button.addEventListener('click', () => {
        const postProcessorManager = PostProcessorManager.getInstance();
        // Use dynamic method call
        const isActive = (postProcessorManager as any)[methodName]();
        
        if (isActive) {
          // When activating effects in the same group, deactivate others
          if (groupPrefix) {
            document.querySelectorAll(`button[id^="${groupPrefix}-"]`).forEach(btn => {
              if (btn.id !== buttonId) {
                btn.classList.remove('active');
              }
            });
          }
          
          button.classList.add('active');
        } else {
          button.classList.remove('active');
        }
      });
    }
  }
  
  // Initialize sound controls
  private initializeSoundControls(): void {
    const soundOnButton = document.getElementById('sound-on');
    const soundOffButton = document.getElementById('sound-off');
    const audioManager = AudioManager.getInstance();
    
    // Set up ambient sound controls
    const windButton = document.getElementById('ambient-wind');
    const droneButton = document.getElementById('ambient-drone');
    
    // Attach event listeners for click and touch events to comply with autoplay policy
    // Audio will only start once the user interacts with the page
    const initAudioOnUserInteraction = () => {
      console.log('User interaction detected, initializing audio...');
      
      // Initialize audio manager on first user interaction
      audioManager.setVolume(0.5); // Default volume
      
      // Remove the event listeners once audio is initialized
      document.removeEventListener('click', initAudioOnUserInteraction);
      document.removeEventListener('touchstart', initAudioOnUserInteraction);
    };
    
    // Add the event listeners
    document.addEventListener('click', initAudioOnUserInteraction, { once: true });
    document.addEventListener('touchstart', initAudioOnUserInteraction, { once: true });
    
    // Add click handlers for the sound buttons
    if (soundOnButton) {
      soundOnButton.addEventListener('click', () => {
        console.log('Sound on button clicked');
        audioManager.setVolume(0.8); // Set to high volume for testing
        soundOnButton.classList.add('active');
        if (soundOffButton) {
          soundOffButton.classList.remove('active');
        }
        
        // Test sound
        setTimeout(() => {
          console.log('Playing test sound...');
          audioManager.playHopSound().then(() => {
            console.log('Test sound completed');
          }).catch(error => {
            console.error('Error playing test sound:', error);
          });
        }, 100);
      });
    }
    
    if (soundOffButton) {
      soundOffButton.addEventListener('click', () => {
        console.log('Sound off button clicked');
        audioManager.setVolume(0); // Mute sound
        soundOffButton.classList.add('active');
        if (soundOnButton) {
          soundOnButton.classList.remove('active');
        }
      });
    }
    
    // Setup ambient sound toggling
    if (windButton) {
      windButton.addEventListener('click', () => {
        // Check if already active
        const isActive = windButton.classList.contains('active');
        
        if (isActive) {
          // Stop wind
          audioManager.stopWind();
          windButton.classList.remove('active');
        } else {
          // Start wind
          audioManager.startWind(0.6);
          windButton.classList.add('active');
        }
      });
    }
    
    if (droneButton) {
      droneButton.addEventListener('click', () => {
        // Check if already active
        const isActive = droneButton.classList.contains('active');
        
        if (isActive) {
          // Stop drone
          audioManager.stopDrone();
          droneButton.classList.remove('active');
        } else {
          // Start drone
          audioManager.startDrone();
          droneButton.classList.add('active');
        }
      });
    }
    
    // Connect weather particle system to audio system
    const particleManager = ParticleManager.getInstance();
    const rainButton = document.getElementById('weather-rain');
    const snowButton = document.getElementById('weather-snow');
    
    if (rainButton) {
      // Update the existing rain button click handler to also control audio
      const originalClickHandler = rainButton.onclick;
      rainButton.onclick = null;
      
      rainButton.addEventListener('click', () => {
        const particleManager = ParticleManager.getInstance();
        
        if (particleManager.weatherType === 'rain') {
          // Stop rain particles
          particleManager.stopWeather();
          // Stop rain sound
          audioManager.stopWeather();
          rainButton.classList.remove('active');
        } else {
          // Clear any existing weather
          if (particleManager.weatherActive) {
            // Remove active class from all weather buttons
            document.querySelectorAll('button[id^="weather-"]').forEach(btn => {
              btn.classList.remove('active');
            });
          }
          
          // Start rain particles
          particleManager.startRain(0.7);
          // Start rain sound
          try {
            console.log('Starting rain sound');
            audioManager.startRain(0.7);
          } catch (error) {
            console.error('Failed to start rain sound:', error);
          }
          rainButton.classList.add('active');
        }
      });
    }
    
    if (snowButton) {
      // Update the existing snow button click handler to also control audio
      const originalClickHandler = snowButton.onclick;
      snowButton.onclick = null;
      
      snowButton.addEventListener('click', () => {
        const particleManager = ParticleManager.getInstance();
        
        if (particleManager.weatherType === 'snow') {
          // Stop snow particles
          particleManager.stopWeather();
          // Stop snow sound
          audioManager.stopWeather();
          snowButton.classList.remove('active');
        } else {
          // Clear any existing weather
          if (particleManager.weatherActive) {
            // Remove active class from all weather buttons
            document.querySelectorAll('button[id^="weather-"]').forEach(btn => {
              btn.classList.remove('active');
            });
          }
          
          // Start snow particles
          particleManager.startSnow(0.5);
          // Start snow sound
          try {
            console.log('Starting snow sound');
            audioManager.startSnow(0.5);
          } catch (error) {
            console.error('Failed to start snow sound:', error);
          }
          snowButton.classList.add('active');
        }
      });
    }
  }
  
  
  // Initialize speed control UI and event handlers
  private initializeSpeedControls() {
    // Get the speed control elements
    this.pauseButton = document.getElementById('pause-btn');
    this.speedButtons['0.5'] = document.getElementById('speed-05x');
    this.speedButtons['1'] = document.getElementById('speed-1x');
    this.speedButtons['5'] = document.getElementById('speed-5x');
    
    // Add click event listener to pause button
    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', () => {
        this.gamePaused = !this.gamePaused;
        this.updatePauseButtonUI();
      });
    }
    
    // Add click event listeners to speed buttons
    for (const [speedStr, button] of Object.entries(this.speedButtons)) {
      if (button) {
        button.addEventListener('click', () => {
          const speed = parseFloat(speedStr);
          this.setGameSpeed(speed);
        });
      }
    }
  }
  
  // Update the pause button UI based on game state
  private updatePauseButtonUI() {
    if (this.pauseButton) {
      if (this.gamePaused) {
        this.pauseButton.textContent = '▶ Play';
        this.pauseButton.classList.add('paused');
      } else {
        this.pauseButton.textContent = '⏸ Pause';
        this.pauseButton.classList.remove('paused');
      }
    }
  }
  
  // Set the game speed and update UI
  private setGameSpeed(speed: number) {
    // Set the game speed
    this.gameSpeed = speed;
    
    // If setting a speed, ensure game is not paused
    if (speed > 0 && this.gamePaused) {
      this.gamePaused = false;
      this.updatePauseButtonUI();
    }
    
    // Update the active state of speed buttons
    for (const [speedStr, button] of Object.entries(this.speedButtons)) {
      if (button) {
        if (parseFloat(speedStr) === speed) {
          button.classList.add('active');
        } else {
          button.classList.remove('active');
        }
      }
    }
    
    console.log(`Game speed set to ${speed}x`);
  }
  
  // Create UI elements
  private createUI(engine: ex.Engine) {
    // Stats display (fixed to screen)
    this.statsText = new ex.Label({
      text: 'Entities: 0',
      pos: new ex.Vector(10, 10),
      font: new ex.Font({
        family: 'VT323, monospace',
        size: 16,
        unit: ex.FontUnit.Px
      }),
      color: ex.Color.fromHex('#33ff33') // Matching the terminal green color
    });
    this.statsText.z = 100; // Ensure it's on top
    this.statsText.fixed = true; // Make it fixed to the screen (not affected by camera)
    
    // Add background for stats text
    const statsBg = new ex.Actor({
      pos: new ex.Vector(150, 10),
      width: 280,
      height: 24,
      color: ex.Color.fromHex('#000000'),
      z: 99
    });
    statsBg.fixed = true;
    statsBg.graphics.use(new ex.Rectangle({
      width: 280,
      height: 24,
      color: ex.Color.fromHex('#000000'),
      strokeColor: ex.Color.fromHex('#5f5f5f'),
      lineWidth: 1
    }));
    
    this.add(statsBg);
    this.add(this.statsText);
    
    // FPS counter
    this.fpsCounter = new ex.Label({
      text: 'FPS: 0',
      pos: new ex.Vector(engine.drawWidth - 70, 10),
      font: new ex.Font({
        family: 'VT323, monospace',
        size: 16,
        unit: ex.FontUnit.Px
      }),
      color: ex.Color.fromHex('#aaaaff') // Matching the blue color
    });
    this.fpsCounter.z = 100;
    this.fpsCounter.fixed = true; // Make it fixed to the screen
    
    // Add background for FPS counter
    const fpsBg = new ex.Actor({
      pos: new ex.Vector(engine.drawWidth - 40, 10),
      width: 80,
      height: 24,
      color: ex.Color.fromHex('#000000'),
      z: 99
    });
    fpsBg.fixed = true;
    fpsBg.graphics.use(new ex.Rectangle({
      width: 80,
      height: 24,
      color: ex.Color.fromHex('#000000'),
      strokeColor: ex.Color.fromHex('#5f5f5f'),
      lineWidth: 1
    }));
    
    this.add(fpsBg);
    this.add(this.fpsCounter);
    
    // Debug toggle button
    this.debugToggle = new ex.Actor({
      pos: new ex.Vector(engine.drawWidth - 30, 40),
      width: 20,
      height: 20,
      color: ex.Color.fromHex('#000000') // Black background
    });
    this.debugToggle.z = 100;
    this.debugToggle.fixed = true; // Make it fixed to the screen
    
    // Create a rectangle for the debug toggle with border
    this.debugToggle.graphics.use(new ex.Rectangle({
      width: 20,
      height: 20,
      color: ex.Color.fromHex('#000000'),
      strokeColor: ex.Color.fromHex('#5f5f5f'),
      lineWidth: 1
    }));
    
    // Add a label inside the toggle button
    const debugLabel = new ex.Label({
      text: 'D',
      pos: new ex.Vector(0, 0),
      font: new ex.Font({
        family: 'VT323, monospace',
        size: 16,
        unit: ex.FontUnit.Px
      }),
      color: this.debugMode ? ex.Color.fromHex('#33ff33') : ex.Color.fromHex('#888888'),
      textAlign: ex.TextAlign.Center
    });
    this.debugToggle.addChild(debugLabel);
    
    this.add(this.debugToggle);
    
    // Make the toggle button clickable
    this.debugToggle.on('pointerdown', () => {
      this.debugMode = !this.debugMode;
      
      // Update the label color
      debugLabel.color = this.debugMode ? ex.Color.fromHex('#33ff33') : ex.Color.fromHex('#888888');
      
      // Draw or clear debug grid
      if (this.debugMode) {
        this.grid.drawDebugGrid();
      } else {
        // TODO: Clear debug grid
      }
    });
    
    // No need to attach to camera since we're using fixed=true
    // These will automatically stay fixed to the screen
  }
  
  // Set up input handlers
  private setupInputHandlers(engine: ex.Engine) {
    // Handle keyboard shortcuts
    engine.input.keyboard.on('down', (evt: ex.KeyEvent) => {
      // Toggle debug mode with F3
      if (evt.key === ex.Keys.F3) {
        this.debugMode = !this.debugMode;
        this.debugToggle.color = this.debugMode ? ex.Color.Green : ex.Color.Gray;
        
        if (this.debugMode) {
          this.grid.drawDebugGrid();
        } else {
          // TODO: Clear debug grid
        }
      }
      
      // Zoom to fit world with F2
      if (evt.key === ex.Keys.F2) {
        this.cameraController.focusOnCell(Math.floor(GRID_WIDTH / 2), Math.floor(GRID_HEIGHT / 2));
      }
      
      // Add a random animal at the current mouse position when '1' key is pressed
      if (evt.key === ex.Keys.Digit1 || evt.key === ex.Keys.Numpad1) {
        // Get the current mouse position
        const mousePos = engine.input.pointers.primary.lastWorldPos;
        
        // Convert to grid coordinates
        const gridX = Math.floor(mousePos.x / TILE_SIZE);
        const gridY = Math.floor(mousePos.y / TILE_SIZE);
        
        // Check if the position is valid and empty
        if (gridX >= 0 && gridX < GRID_WIDTH && gridY >= 0 && gridY < GRID_HEIGHT && 
            this.grid.isValidMove(gridX, gridY)) {
          console.log(`'1' key pressed, adding random animal at cursor (${gridX}, ${gridY})`);
          
          // Create random animal
          const animalTypes = Object.values(AnimalType);
          const randomIndex = Math.floor(Math.random() * animalTypes.length);
          const randomAnimalType = animalTypes[randomIndex];
          
          console.log(`Creating ${randomAnimalType} at (${gridX}, ${gridY})`);
          
          // Create and add the animal
          const animal = AnimalFactory.createAnimal(randomAnimalType, gridX, gridY, this.grid);
          this.grid.addEntity(animal, gridX, gridY);
          this.add(animal);
          this.worldEntities.push(animal);
          
          // Auto-follow the newly created animal
          console.log("Auto-following newly created animal");
          this.cameraController.followEntity(animal);
        } else {
          console.log("Cannot add animal: position invalid or occupied");
        }
      }
    });
    
    // Handle mouse clicks on grid cells
    engine.input.pointers.primary.on('down', (evt: ex.PointerEvent) => {
      // Use worldPos directly from the event
      const worldPos = evt.worldPos;
      
      // Convert to grid coordinates
      const gridX = Math.floor(worldPos.x / TILE_SIZE);
      const gridY = Math.floor(worldPos.y / TILE_SIZE);
      
      console.log(`Click detected at grid position (${gridX}, ${gridY})`);
      
      // Check if within grid bounds
      if (gridX >= 0 && gridX < GRID_WIDTH && gridY >= 0 && gridY < GRID_HEIGHT) {
        // Check if there's an entity at this position
        const entity = this.grid.getEntityAt(gridX, gridY);
        
        if (entity) {
          console.log(`Clicked on entity at (${gridX}, ${gridY}):`, entity);
          
          // Log details based on entity type
          if (entity instanceof Animal) {
            console.log(`Animal type: ${entity.animalType}, Behaviors: ${entity.behaviors.join(', ')}`);
          } else if (entity instanceof Plant) {
            console.log(`Plant type: ${entity.plantType}, Growth: ${entity.growthStage}/${entity.maxGrowthStage}`);
          }
          
          // Follow any entity type with the camera
          console.log("Following entity with camera");
          this.cameraController.followEntity(entity);
        } else {
          console.log(`Clicked on empty cell (${gridX}, ${gridY})`);
        }
      }
    });
  }
  
  // Populate the world with plants
  private populatePlants() {
    const plants = PlantFactory.populateWorld(this.grid);
    
    // Add plants to the scene
    for (const plant of plants) {
      this.add(plant);
      this.worldEntities.push(plant);
    }
    
    console.log(`Created ${plants.length} plants`);
  }
  
  // Populate the world with animals
  private populateAnimals() {
    // Create land animals across the map with low density
    const landAnimals = AnimalFactory.populateArea(
      this.grid,
      1, 1,
      GRID_WIDTH - 2, GRID_HEIGHT - 2,
      0.03 // 3% density since we increased the map size
    );
    
    // Add land animals to the scene
    for (const animal of landAnimals) {
      this.add(animal);
      this.worldEntities.push(animal);
    }
    
    // Populate water with fish specifically
    const fish = AnimalFactory.populateWaterWithFish(this.grid, 0.08); // 8% density in water
    
    // Add fish to the scene
    for (const fishAnimal of fish) {
      this.add(fishAnimal);
      this.worldEntities.push(fishAnimal);
    }
    
    console.log(`Created ${landAnimals.length} land animals and ${fish.length} fish`);
  }

  // Update game state (called each frame)
  public update(engine: ex.Engine, delta: number) {
    super.update(engine, delta);
    
    // Update camera - always update camera even when paused
    this.cameraController.update(engine, delta);
    
    // Always update the movement system for smooth animations
    MovementSystem.getInstance().update();
    
    // Always update particle systems for smooth animations
    ParticleManager.getInstance().update(delta);
    
    // Always update post processors
    PostProcessorManager.getInstance().update(delta);
    
    // Always update emotion system for reaction displays
    EmotionSystem.getInstance().update(delta);
    
    // Process entity actions only if not paused
    if (this.gameRunning && !this.gamePaused) {
      // Simplified game speed control for better performance
      const processChance = this.gameSpeed;
      // Only process entities if the random roll is successful based on game speed
      if (Math.random() <= processChance) {
        this.processEntityActions();
      }
    }
    
    // Update UI - always update UI
    this.updateUI(engine);
  }
  
  // Process entity actions
  private processEntityActions() {
    const currentTime = Date.now();
    
    // Reset all object pools before processing entities
    GridPositionPool.getInstance().resetPool();
    ActionPool.getInstance().resetPools();
    
    // Process each entity
    for (const entity of this.worldEntities) {
      const action = entity.getNextAction();
      
      if (action) {
        action.execute();
      }
    }
  }
  
  // Cached UI vector to avoid allocations
  private uiVector1 = new ex.Vector(10, 10);
  private uiVector2 = new ex.Vector(0, 10);
  private uiVector3 = new ex.Vector(0, 40);
  
  // Tracking last UI update time for throttling
  private lastUIUpdateTime = 0;

  // Update UI elements less frequently for better performance
  private updateUI(engine: ex.Engine) {
    const currentTime = engine.clock.elapsed();
    
    // Only update UI twice per second to reduce overhead
    if (currentTime - this.lastUIUpdateTime < 0.5) {
      return;
    }
    
    this.lastUIUpdateTime = currentTime;
    
    // Count entities without creating new arrays
    let animalCount = 0;
    let plantCount = 0;
    
    for (const entity of this.worldEntities) {
      if (entity instanceof Animal) animalCount++;
      else if (entity instanceof Plant) plantCount++;
    }
    
    // Add the game speed to the stats display
    const speedStatus = this.gamePaused ? 'PAUSED' : `${this.gameSpeed}×`;
    this.statsText.text = `ANIMALS:${animalCount} PLANTS:${plantCount} SPD:${speedStatus}`;
    
    // Update FPS counter
    this.fpsCounter.text = `FPS:${Math.round(engine.stats.currFrame.fps)}`;
    
    // Fix UI positions - use fixed screen coordinates
    const viewport = engine.screen.viewport;
    
    // Position stats text correctly within its background
    this.uiVector1.x = 10; 
    this.uiVector1.y = 15; // Center vertically
    this.statsText.pos = this.uiVector1;
    
    // Position FPS counter
    this.uiVector2.x = viewport.width - 40;
    this.uiVector2.y = 15; // Center vertically
    this.fpsCounter.pos = this.uiVector2;
    
    // Position debug toggle
    this.uiVector3.x = viewport.width - 30;
    this.uiVector3.y = 45; // Position lower with consistent spacing
    this.debugToggle.pos = this.uiVector3;
  }
}